// Pure staged-edits logic.
//
// applyStagedEdits clones a catalogue and overlays staged cell values
// so the live Marketing-Surface Preview re-aggregates against the
// candidate cube. The function is rune-free so it can be covered by
// node:test without the Svelte compiler. The reactive store sits in
// staged-edits.svelte.ts and depends on this module.

import type {
  CubeCellValue,
  EstablishmentFeeOverride,
  ProductCatalogueFile,
  RateCube,
  StagedChangesPayload,
  Jurisdiction,
  ProductId,
  CellDelta,
  ValueType
} from './types';
import type { CubeCoordinate } from './cube';
import { parseBureauCoordinate, parseGradeCoordinate } from './cube';

export interface StagedCellEdit {
  coordinates: string;          // e.g. "950_plus / HomeOwner / hLoan"
  before: CubeCellValue;        // value at the time the user first staged the edit
  staged: Partial<CubeCellValue>; // overlayed onto before to compute the staged cell
  field?: 'interest_rate_pa' | 'max_loan_amount';
}

export type AmountField = 'min' | 'max' | 'increment';

export interface StagedAmountEdit {
  field: AmountField;
  before: number;
  staged: number;
}

export interface LoanPurpose {
  id: string;
  display_name: string;
  url_slug?: string;
  amount_range?: { min: number; max: number };
  _internal_only?: boolean;
}

// One staged change to the loan_purposes array, keyed by purpose_id.
// 'add' has no before; 'remove' has no staged; 'update' has both.
// The store collapses repeated edits to the same id, so the most
// recent intent wins (e.g. an add followed by an update on the same
// id stays as a single 'add' with the merged purpose). This keeps the
// payload at most one entry per id and matches the server's diff
// semantics in catalogue-client.diffNonRateFields.
export type StagedPurposeEdit =
  | { action: 'add'; purpose_id: string; staged: LoanPurpose }
  | { action: 'remove'; purpose_id: string; before: LoanPurpose }
  | { action: 'update'; purpose_id: string; before: LoanPurpose; staged: LoanPurpose };

// Well-known fee paths the editor manipulates. The server-side
// diffNonRateFields in catalogue-client carries the same list; both
// sides enumerate explicitly because the fees object is a small
// fixed schema rather than a free-form tree.
export type FeePath =
  | 'fees.establishment_fee'
  | 'fees.dishonour_fee'
  | 'fees.overdue_fee'
  | 'fees.included_in_comparison_rate';

export interface StagedFeeEdit {
  path: FeePath;
  before: unknown;
  staged: unknown;
}

export interface EstablishmentFeeTier {
  min_amount: number;
  max_amount: number;
  fee: number;
}

export interface FeesShape {
  schedule_id?: string;
  establishment_fee?: { tiers: EstablishmentFeeTier[] };
  dishonour_fee?: number;
  overdue_fee?: number;
  included_in_comparison_rate?: string[];
}

// One staged edit of the per-credit-rating establishment-fee override.
// `staged: null` means the user staged the removal of the override; a
// non-null `staged` either creates (when `before` is null) or updates the
// override in place. The store keys these edits by credit_rating_id so a
// repeated edit on the same band/grade collapses to the latest intent.
export interface StagedEstablishmentFeeOverrideEdit {
  credit_rating_id: string;
  before: EstablishmentFeeOverride | null;
  staged: EstablishmentFeeOverride | null;
}

// One staged edit of terms_months. The server contract carries the full
// replacement list (term_edits: number[]), not a diff, so the store keeps
// a single before / staged pair rather than per-term records. The store
// drops the entry when staged equals before so a revert leaves no
// residual intent.
export interface StagedTermsEdit {
  before: number[];
  staged: number[];
}

export interface StagedSnapshot {
  jurisdiction: Jurisdiction;
  product: ProductId;
  draftSlug: string;
  valueType: ValueType | null;
  // lint:edit-category-partial-ok — cube_edits is `edits` below (legacy
  // naming from before the other *_edits buckets existed). Renaming to
  // align with the lint is SPEC-CHANGE-CANDIDATES item 16 option 3.
  edits: Record<string, StagedCellEdit>;
  amount_edits: Partial<Record<AmountField, StagedAmountEdit>>;
  purpose_edits: Record<string, StagedPurposeEdit>;
  fee_edits: Partial<Record<FeePath, StagedFeeEdit>>;
  fee_override_edits: Record<string, StagedEstablishmentFeeOverrideEdit>;
  term_edits: StagedTermsEdit | null;
  // Last commit SHA the store observed at the draft branch tip — sent
  // on the next saveDraft as expected_commit_sha so the server can
  // refuse a write into drifted state. Null on a fresh slug.
  lastObservedCommitSha: string | null;
}

// JSON round-trip rather than structuredClone — the catalogue arrives
// from the catalogue store as a Svelte 5 $state proxy, and proxies are
// not structurally cloneable (DataCloneError). The catalogue is plain
// JSON data (no Date / Map / Set) so JSON.parse(JSON.stringify(...))
// produces a clean deep copy that strips the proxy.
function clonePlainObject<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function applyStagedEditsToCatalogue(catalogue: ProductCatalogueFile, edits: Record<string, StagedCellEdit>): ProductCatalogueFile {
  const next: ProductCatalogueFile = clonePlainObject(catalogue);
  const cube = next.rate_cube as RateCube;
  const pricingModel = next._dimension_refs?.pricing_model;
  for (const edit of Object.values(edits)) {
    if (pricingModel === 'bureau_band_x_housing_x_channel') {
      const c = parseBureauCoordinate(edit.coordinates);
      const node = cube[c.bureauBand]?.[c.housing];
      if (!node) continue;
      const existing = node[c.channel];
      node[c.channel] = { ...(existing ?? { interest_rate_pa: null, max_loan_amount: null }), ...edit.staged };
    } else if (pricingModel === 'grade_x_subgrade_x_channel') {
      const c = parseGradeCoordinate(edit.coordinates);
      const node = (cube[c.gradeSubgrade] as unknown as Record<string, CubeCellValue> | undefined);
      if (!node) continue;
      const existing = node[c.channel];
      node[c.channel] = { ...(existing ?? { interest_rate_pa: null, max_loan_amount: null }), ...edit.staged };
    }
  }
  return next;
}

export function stagedEditsToPayload(
  snapshot: StagedSnapshot
): StagedChangesPayload {
  const payload: StagedChangesPayload = {
    jurisdiction: snapshot.jurisdiction,
    product: snapshot.product
  };
  // lint:edit-category-partial-ok — cube_edits is read from `snapshot.edits` (the SPA store predates the *_edits suffix convention for that field).
  const cubeEditEntries = Object.values(snapshot.edits);
  if (cubeEditEntries.length > 0) {
    payload.cube_edits = cubeEditEntries.map((edit) => ({
      coordinates: edit.coordinates,
      new_value: edit.staged
    }));
  }
  const amountEditEntries = Object.values(snapshot.amount_edits).filter(
    (edit): edit is StagedAmountEdit => edit !== undefined
  );
  if (amountEditEntries.length > 0) {
    payload.amount_edits = amountEditEntries.map((edit) => ({
      field: edit.field,
      new_value: edit.staged
    }));
  }
  const purposeEditEntries = Object.values(snapshot.purpose_edits ?? {});
  if (purposeEditEntries.length > 0) {
    payload.purpose_edits = purposeEditEntries.map((edit) => {
      if (edit.action === 'add') return { action: 'add' as const, purpose: edit.staged };
      if (edit.action === 'remove') return { action: 'remove' as const, purpose_id: edit.purpose_id };
      return { action: 'update' as const, purpose_id: edit.purpose_id, purpose: edit.staged };
    });
  }
  const feeEditEntries = Object.values(snapshot.fee_edits ?? {}).filter(
    (edit): edit is StagedFeeEdit => edit !== undefined
  );
  if (feeEditEntries.length > 0) {
    payload.fee_edits = feeEditEntries.map((edit) => ({
      path: edit.path,
      new_value: edit.staged
    }));
  }
  const overrideEditEntries = Object.values(snapshot.fee_override_edits ?? {});
  if (overrideEditEntries.length > 0) {
    payload.fee_override_edits = overrideEditEntries.map((edit) => ({
      credit_rating_id: edit.credit_rating_id,
      new_value: edit.staged
    }));
  }
  if (snapshot.term_edits) {
    payload.term_edits = [...snapshot.term_edits.staged].sort((a, b) => a - b);
  }
  return payload;
}

// Retained for callers that explicitly only care about cube edits.
// Equivalent to stagedEditsToPayload when only cube edits are staged.
export function stagedCubeEditsToPayload(
  snapshot: StagedSnapshot
): StagedChangesPayload {
  return stagedEditsToPayload(snapshot);
}

export function deltasFromStaged(snapshot: StagedSnapshot): CellDelta[] {
  const out: CellDelta[] = [];
  for (const edit of Object.values(snapshot.edits)) {
    if (edit.staged.interest_rate_pa !== undefined) {
      const before = edit.before.interest_rate_pa;
      const after = edit.staged.interest_rate_pa;
      out.push({
        coordinates: edit.coordinates,
        field: 'interest_rate_pa',
        before,
        after,
        delta: before !== null && after !== null ? Number(after) - Number(before) : null
      });
    }
    if (edit.staged.max_loan_amount !== undefined) {
      const before = edit.before.max_loan_amount;
      const after = edit.staged.max_loan_amount;
      out.push({
        coordinates: edit.coordinates,
        field: 'max_loan_amount',
        before,
        after,
        delta: before !== null && after !== null ? Number(after) - Number(before) : null
      });
    }
  }
  return out;
}

// sessionStorage key for the staged-edits buffer. Scoped per identity
// so a browser session that switches between identities (a NETEVO and
// pilot-Harmoney testing reality) does not rehydrate one identity's
// draft work into another's editor. The proxy Worker enforces role
// gates server-side regardless; this key isolation keeps the local
// state model honest.
//
// Returns null when no identity is loaded yet — callers treat that as
// "do not persist" rather than falling back to a shared key.
export const STAGED_EDITS_STORAGE_PREFIX = 'maintenance.staged-edits';
export const LEGACY_STAGED_EDITS_STORAGE_KEY = 'maintenance.staged-edits';

export function stagedEditsStorageKey(email: string | null): string | null {
  if (!email) return null;
  return `${STAGED_EDITS_STORAGE_PREFIX}:${email}`;
}

// Naming heuristic for the draft slug. Combines product + a timestamp
// + a short random suffix so two sessions opening Detail under the
// same identity in the same second cannot collide on the slug.
//
// Pre-fix the slug used minute precision (slice(0,12)) which was
// vulnerable to cross-session collision: two tabs (or a test session
// impersonating a real identity via the dev drawer + the operator's
// real session) starting within the same UTC minute would generate
// the same slug. saveDraft would then read the prior session's draft
// branch as startingCube and silently merge the prior edits into the
// new session's intent — surfaced by a calculator-test failure when
// the prior session's establishment_fee_override removal leaked into
// a rate-only proposal (proposed branch
// proposed/au-unsecured-personal-loan-2026-05-21-001, closed without
// merging).
//
// Seconds precision plus a 4-character random suffix gives O(60M)
// distinct slugs per second — collision-resistant for any plausible
// concurrent-session pattern. The salt parameter (used by unit tests)
// keeps the slug deterministic when supplied; without it, the
// suffix is freshly random per call.
export function defaultDraftSlug(jurisdiction: Jurisdiction, product: ProductId, salt?: string): string {
  const stamp = (salt ?? new Date().toISOString())
    .replace(/[-:T.Z]/g, '')
    .slice(0, 14)
    .toLowerCase();
  const suffix = salt
    ? 'test'
    : Math.random().toString(36).slice(2, 6).padEnd(4, '0');
  return `${jurisdiction}-${product}-${stamp}-${suffix}`;
}

// Detect monotonicity violations on the bureau-band axis. Returns a
// list of human-readable warnings for the editor to surface inline.
// Limited to the bureau-band-monotonicity rule for the first pass;
// the symmetry checker on the server side runs the full §10.2 suite
// as part of validateChange.
// Named properties that sit alongside the cube-axis keys at the credit-
// rating-axis-level node. Keep in sync with the schema's
// `$defs/cube_outer_level.properties`, `tools/validate.mjs`
// `CREDIT_RATING_LEVEL_METADATA_KEYS`, and `tools/catalogue-client.mjs`.
export const CREDIT_RATING_LEVEL_METADATA_KEYS: ReadonlySet<string> = new Set([
  'establishment_fee_override'
]);

export function bureauBandWarnings(catalogue: ProductCatalogueFile, edits: Record<string, StagedCellEdit>, channel: string): string[] {
  if (catalogue._dimension_refs?.pricing_model !== 'bureau_band_x_housing_x_channel') return [];
  const staged = applyStagedEditsToCatalogue(catalogue, edits);
  const bands = Object.keys(staged.rate_cube);
  const warnings: string[] = [];
  const firstBandKeys = Object.keys(staged.rate_cube[bands[0]!] ?? {});
  for (const housing of firstBandKeys) {
    if (CREDIT_RATING_LEVEL_METADATA_KEYS.has(housing)) continue;
    let last: number | null = null;
    for (let i = 0; i < bands.length; i += 1) {
      const band = bands[i]!;
      const rate = staged.rate_cube[band]?.[housing]?.[channel]?.interest_rate_pa;
      if (rate === null || rate === undefined) continue;
      if (last !== null && rate < last) {
        warnings.push(
          `Bureau-band monotonicity: ${band} / ${housing} / ${channel} rate ${rate} is below ${bands[i - 1]} (${last}). Rates must non-decrease as bands worsen.`
        );
      }
      last = rate;
    }
  }
  return warnings;
}
